/* Retired reward cleanup only. Never adds merchandise, changes offers, or intercepts checkout.
 * The dedicated reward product must also be unavailable server-side before the offer expires.
 */
(function (root) {
  'use strict';
  const PRODUCT_ID = 9036472090703;
  const VARIANT_ID = 46563960291407;
  const SOURCE = 'skrt-retired-gift';

  function retiredLines(cart) {
    if (!cart || !Array.isArray(cart.items)) throw new Error('Invalid cart response');
    return cart.items.filter((line) => Number(line.product_id) === PRODUCT_ID &&
      Number(line.variant_id) === VARIANT_ID && typeof line.key === 'string' && line.key.length > 0);
  }

  /** Create a dependency-free, serialized cleanup controller. No cart data is persisted. */
  function createController(win, options = {}) {
    const doc = win.document;
    const requestMs = options.requestMs || 8000;
    const debounceMs = options.debounceMs || 150;
    const cooldownMs = options.cooldownMs || 30000;
    const maxFailures = options.maxFailures || 3;
    let running = null, timer = null, failures = 0, lastFailure = -Infinity, installed = false;
    let lastVerifiedClean = -Infinity;
    let unsubscribe = null, readyStarted = false;
    const listeners = [];
    const now = () => win.Date ? win.Date.now() : Date.now();

    function urlFor(path) {
      const origin = win.location.origin;
      const candidate = new URL(win.Shopify && win.Shopify.routes && win.Shopify.routes.root || '/', origin);
      const base = candidate.origin === origin ? candidate : new URL('/', origin);
      if (!base.pathname.endsWith('/')) base.pathname += '/';
      return new URL(path, base);
    }

    async function request(path, body) {
      const controller = new AbortController();
      let timeout;
      const expired = new Promise((resolve, reject) => {
        timeout = win.setTimeout(() => {
          controller.abort();
          reject(new Error('Retired gift request timed out'));
        }, requestMs);
      });
      const fetchResult = (async () => {
        const response = await win.fetch(urlFor(path).href, {
          method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store',
          headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
          ...(body ? { body: JSON.stringify(body) } : {}), signal: controller.signal
        });
        if (!response.ok) throw new Error('Retired gift request failed');
        return response.json();
      })();
      try { return await Promise.race([fetchResult, expired]); }
      finally { win.clearTimeout(timeout); }
    }

    async function refreshViews(cart) {
      let refreshError = null;
      const main = doc.querySelector('main-cart[data-section-id]');
      if (main) {
        try {
          const id = main.getAttribute('data-section-id');
          const path = new URL('cart', urlFor(''));
          path.searchParams.set('sections', id);
          const sections = await request(path.href);
          if (typeof sections[id] !== 'string') throw new Error('Cart section unavailable');
          const parsed = new win.DOMParser().parseFromString(sections[id], 'text/html');
          const current = doc.getElementById('shopify-section-' + id);
          const next = parsed.getElementById('shopify-section-' + id);
          if (!current || !next) throw new Error('Cart section missing');
          current.innerHTML = next.innerHTML;
        } catch (error) { refreshError = error; }
      }
      const pubsub = win.theme && win.theme.pubsub;
      if (pubsub && pubsub.PUB_SUB_EVENTS) {
        pubsub.publish(pubsub.PUB_SUB_EVENTS.cartUpdate, { source: SOURCE, cart });
      }
      doc.dispatchEvent(new win.CustomEvent('cart:refresh', {
        bubbles: true, detail: { open: false, source: SOURCE }
      }));
      if (refreshError) throw refreshError;
    }

    async function reconcile() {
      if (running) return running;
      if (failures >= maxFailures || now() - lastFailure < cooldownMs) return { status: 'cooldown' };
      const operations = win.skrtCartOperations;
      // Never fall back to an uncoordinated cart mutation if the shared queue is unavailable.
      if (!operations || typeof operations.run !== 'function') return { status: 'queue-unavailable' };
      if (operations.pendingIds && operations.pendingIds.length) return { status: 'shopper-operation-pending' };
      running = (async () => {
        try {
          return await operations.run(async () => {
            const before = await request('cart.js');
            const lines = retiredLines(before);
            if (!lines.length) { lastVerifiedClean = now(); return { status: 'clean' }; }
            const updates = Object.fromEntries(lines.map((line) => [line.key, 0]));
            await request('cart/update.js', { updates });
            const after = await request('cart.js');
            if (retiredLines(after).length) throw new Error('Retired gift remains');
            lastVerifiedClean = now();
            await refreshViews(after);
            return { status: 'removed', removedLines: lines.length };
          });
        } catch (error) {
          failures += 1;
          lastFailure = now();
          // No checkout block, timer retry, price edit, or automatic replay after uncertain writes.
          return { status: 'failed' };
        }
      })();
      try { return await running; }
      finally { running = null; }
    }

    function schedule(event) {
      const detail = event && (event.detail || event);
      if (detail && detail.source === SOURCE) return;
      if (doc.hidden || failures >= maxFailures || now() - lastFailure < cooldownMs) return;
      const eventCart = detail && detail.cart;
      const hasCompleteCart = eventCart && Array.isArray(eventCart.items);
      if (hasCompleteCart && !retiredLines(eventCart).length) {
        lastVerifiedClean = now();
        win.clearTimeout(timer); timer = null;
        return;
      }
      // Concept emits several events for the same operation. Its full fresh cart payload
      // proves clean state; suppress its following payload-free refresh/end events.
      // An explicit retired-item payload always bypasses this short deduplication window.
      if (!hasCompleteCart && now() - lastVerifiedClean < 1000) return;
      win.clearTimeout(timer);
      timer = win.setTimeout(() => { timer = null; void reconcile(); }, debounceMs);
    }

    function listen(target, type, handler) {
      target.addEventListener(type, handler);
      listeners.push(() => target.removeEventListener(type, handler));
    }

    function install() {
      if (installed) return;
      installed = true;
      for (const event of ['skrt:cart-operation-end', 'cart:updated', 'ajaxProduct:added', 'cart:refresh', 'shopify:section:load']) {
        listen(doc, event, schedule);
      }
      listen(doc, 'visibilitychange', () => { if (!doc.hidden) schedule(); });
      listen(win, 'pageshow', (event) => { if (event.persisted) schedule(); });
      const ready = () => {
        if (readyStarted) return;
        readyStarted = true;
        const pubsub = win.theme && win.theme.pubsub;
        if (pubsub && pubsub.PUB_SUB_EVENTS && typeof pubsub.subscribe === 'function') {
          unsubscribe = pubsub.subscribe(pubsub.PUB_SUB_EVENTS.cartUpdate, schedule);
        }
        const loader = doc.querySelector('[data-skrt-retired-gift-cleanup]');
        // The listener stays installed for subsequent cart additions even if this page starts empty.
        if (!loader || loader.getAttribute('data-initial-cart-count') !== '0') schedule();
      };
      // This deferred asset precedes theme.js. readyState can already be interactive
      // while later deferred scripts have not installed the cart pubsub service yet.
      if (doc.readyState !== 'complete') listen(doc, 'DOMContentLoaded', ready);
      else ready();
    }

    function destroy() {
      win.clearTimeout(timer);
      for (const remove of listeners) remove();
      if (typeof unsubscribe === 'function') unsubscribe();
    }
    return Object.freeze({ reconcile, install, destroy });
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { retiredLines, createController, PRODUCT_ID, VARIANT_ID, SOURCE };
    return;
  }
  if (!root || root.__skrtRetiredGiftCleanup) return;
  root.__skrtRetiredGiftCleanup = createController(root);
  root.__skrtRetiredGiftCleanup.install();
})(typeof window === 'undefined' ? null : window);
